<template>
  <transition name="fading">
    <Loading v-if="isLoading" />
  </transition>

  <div class="admin-page wide">
    <div class="page-hero">
      <div>
        <p class="page-kicker">Clinic Comparison</p>
        <h1 class="page-title">Compare Watchable Clinics</h1>
        <p class="page-subtitle">Select clinics and compare usage, yield, quality, consistency, and growth side by side.</p>
      </div>
    </div>

    <section class="dashboard-filter-panel dashboard-filter-panel--dates">
      <div class="form-section">
        <label class="field-label" for="compare-start-date">Start date</label>
        <input id="compare-start-date" type="date" class="ui-input" v-model="filters.start_date" @change="loadClinics">
      </div>
      <div class="form-section">
        <label class="field-label" for="compare-end-date">End date</label>
        <input id="compare-end-date" type="date" class="ui-input" v-model="filters.end_date" @change="loadClinics">
      </div>
    </section>

    <div v-if="pageError" class="alert-message">
      <i class="fas fa-exclamation-circle"></i> {{ pageError }}
    </div>

    <section class="selector-card">
      <div class="section-heading">
        <div>
          <h2 class="section-title">Selected clinics</h2>
          <p class="section-description">Choose up to 8 clinics for a readable comparison.</p>
        </div>
        <span class="badge-soft">{{ selectedClinicIds.length }} selected</span>
      </div>
      <div class="clinic-option-grid">
        <label v-for="clinic in clinics" :key="clinic.location_uuid" class="clinic-option">
          <input
            type="checkbox"
            :checked="selectedClinicIds.includes(clinic.location_uuid)"
            :disabled="!selectedClinicIds.includes(clinic.location_uuid) && selectedClinicIds.length >= 8"
            @change="toggleClinic(clinic.location_uuid, $event.target.checked)"
          >
          <span>
            <strong>{{ clinic.clinic_name || 'Unnamed clinic' }}</strong>
            <small>{{ clinic.organization_name || clinic.city || clinic.state || 'Watchable clinic' }}</small>
          </span>
        </label>
      </div>
    </section>

    <section class="comparison-grid">
      <article class="chart-card chart-card--wide">
        <div class="section-heading">
          <div>
            <h2 class="section-title">Screening volume vs patients</h2>
            <p class="section-description">Shows service load and reach by clinic.</p>
          </div>
        </div>
        <div class="chart-wrap">
          <Bar v-if="selectedRows.length" :data="volumePatientChartData" :options="barChartOptions" />
          <div v-else class="empty-chart">Select at least one clinic.</div>
        </div>
      </article>

      <article class="chart-card">
        <div class="section-heading">
          <div>
            <h2 class="section-title">Clinical yield</h2>
            <p class="section-description">Abnormal and Grade 2 rates by clinic.</p>
          </div>
        </div>
        <div class="chart-wrap chart-wrap--compact">
          <Bar v-if="selectedRows.length" :data="yieldChartData" :options="percentChartOptions" />
          <div v-else class="empty-chart">Select clinics to compare.</div>
        </div>
      </article>

      <article class="chart-card">
        <div class="section-heading">
          <div>
            <h2 class="section-title">Operational quality</h2>
            <p class="section-description">Image quality and active screening days.</p>
          </div>
        </div>
        <div class="chart-wrap chart-wrap--compact">
          <Bar v-if="selectedRows.length" :data="qualityActivityChartData" :options="barChartOptions" />
          <div v-else class="empty-chart">Select clinics to compare.</div>
        </div>
      </article>
    </section>

    <div class="table-card">
      <table class="ui-table mobile-card-table">
        <thead>
          <tr>
            <th class="text-start">Clinic</th>
            <th class="text-start">Screenings</th>
            <th class="text-start">Patients</th>
            <th class="text-start">Abnormal</th>
            <th class="text-start">Grade 2</th>
            <th class="text-start">Quality %</th>
            <th class="text-start">Active days</th>
            <th class="text-start">Growth</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in selectedRows" :key="row.location_uuid">
            <td>
              <span class="table-primary">{{ row.clinic_name || 'Unnamed clinic' }}</span>
              <span class="table-secondary">{{ row.organization_name || row.location_uuid }}</span>
            </td>
            <td>{{ row.metrics?.screening_volume ?? 0 }}</td>
            <td>{{ row.metrics?.unique_patients_screened ?? 0 }}</td>
            <td>{{ formatRate(row.metrics?.abnormal_rate) }}</td>
            <td>{{ formatRate(row.metrics?.grade_2_rate) }}</td>
            <td>{{ formatNullableNumber(row.metrics?.avg_image_quality) }}</td>
            <td>{{ row.metrics?.active_days ?? 0 }}</td>
            <td>{{ formatGrowth(row.metrics?.growth_rate) }}</td>
          </tr>
          <tr v-if="selectedRows.length === 0">
            <td colspan="8">
              <div class="empty-state">Select clinics above to compare performance.</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js';
import { Bar } from 'vue-chartjs';
import Loading from '@/components/Loading.vue';
import { getStateOrgClinics } from '@/services/comparisonServices';

ChartJS.register(Tooltip, Legend, CategoryScale, LinearScale, BarElement);

function toISODate(inputDate) {
  const date = inputDate instanceof Date ? inputDate : new Date(inputDate);
  return date.toISOString().slice(0, 10);
}

export default {
  name: 'StateClinicComparison',
  components: { Loading, Bar },
  data() {
    const today = new Date();
    return {
      filters: {
        start_date: toISODate(new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000)),
        end_date: toISODate(today),
      },
      clinics: [],
      selectedClinicIds: [],
      isLoading: false,
      pageError: '',
      barChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom' } },
        scales: { y: { beginAtZero: true } },
      },
      percentChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom' } },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: (value) => `${value}%`,
            },
          },
        },
      },
    };
  },
  computed: {
    selectedRows() {
      const selected = new Set(this.selectedClinicIds);
      return this.clinics.filter((clinic) => selected.has(clinic.location_uuid));
    },
    selectedLabels() {
      return this.selectedRows.map((row) => row.clinic_name || 'Unnamed clinic');
    },
    volumePatientChartData() {
      return {
        labels: this.selectedLabels,
        datasets: [
          {
            label: 'Screenings',
            data: this.selectedRows.map((row) => row.metrics?.screening_volume || 0),
            backgroundColor: '#0fbab2',
            borderRadius: 8,
          },
          {
            label: 'Unique patients',
            data: this.selectedRows.map((row) => row.metrics?.unique_patients_screened || 0),
            backgroundColor: '#6e2b92',
            borderRadius: 8,
          },
        ],
      };
    },
    yieldChartData() {
      return {
        labels: this.selectedLabels,
        datasets: [
          {
            label: 'Abnormal rate',
            data: this.selectedRows.map((row) => Number(((row.metrics?.abnormal_rate || 0) * 100).toFixed(1))),
            backgroundColor: '#dc362e',
            borderRadius: 8,
          },
          {
            label: 'Grade 2 rate',
            data: this.selectedRows.map((row) => Number(((row.metrics?.grade_2_rate || 0) * 100).toFixed(1))),
            backgroundColor: '#f59e0b',
            borderRadius: 8,
          },
        ],
      };
    },
    qualityActivityChartData() {
      return {
        labels: this.selectedLabels,
        datasets: [
          {
            label: 'Avg quality %',
            data: this.selectedRows.map((row) => Number(((row.metrics?.avg_image_quality || 0) * 100).toFixed(1))),
            backgroundColor: '#0fbab2',
            borderRadius: 8,
          },
          {
            label: 'Active days',
            data: this.selectedRows.map((row) => row.metrics?.active_days || 0),
            backgroundColor: '#64748b',
            borderRadius: 8,
          },
        ],
      };
    },
  },
  created() {
    this.loadClinics();
  },
  methods: {
    formatRate(value) {
      return `${(Number(value || 0) * 100).toFixed(1)}%`;
    },
    formatGrowth(value) {
      if (value === null || value === undefined) return '-';
      return `${(Number(value) * 100).toFixed(1)}%`;
    },
    formatNullableNumber(value) {
      if (value === null || value === undefined) return '-';
      return `${(Number(value) * 100).toFixed(0)}%`;
    },
    toggleClinic(locationUuid, checked) {
      if (checked) {
        this.selectedClinicIds = [...new Set([...this.selectedClinicIds, locationUuid])].slice(0, 8);
        return;
      }
      this.selectedClinicIds = this.selectedClinicIds.filter((uuid) => uuid !== locationUuid);
    },
    async loadClinics() {
      this.isLoading = true;
      this.pageError = '';
      try {
        const result = await getStateOrgClinics({ ...this.filters, sort: 'screening_volume', order: 'desc' });
        this.clinics = result.data || [];
        this.selectedClinicIds = this.clinics.slice(0, 5).map((clinic) => clinic.location_uuid);
      } catch (error) {
        this.pageError = error.response?.data?.detail || 'Unable to load clinic comparison data.';
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.selector-card,
.chart-card {
  padding: 18px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius);
  background: #ffffff;
  box-shadow: var(--ui-shadow);
}

.selector-card {
  margin-bottom: 18px;
}

.clinic-option-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.clinic-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
  padding: 12px;
  border: 1px solid var(--ui-border);
  border-radius: 10px;
  background: #f8fafc;
}

.clinic-option span {
  min-width: 0;
}

.clinic-option strong,
.clinic-option small {
  display: block;
}

.clinic-option strong {
  color: var(--ui-text);
  font-size: 0.88rem;
}

.clinic-option small {
  margin-top: 3px;
  color: var(--ui-muted);
}

.comparison-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  margin-bottom: 18px;
}

.chart-card--wide {
  grid-column: 1 / -1;
}

.chart-wrap {
  min-height: 320px;
  position: relative;
}

.chart-wrap--compact {
  min-height: 280px;
}

.empty-chart {
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed var(--ui-border);
  border-radius: var(--ui-radius);
  color: var(--ui-muted);
}

@media (max-width: 960px) {
  .clinic-option-grid,
  .comparison-grid {
    grid-template-columns: 1fr;
  }
}
</style>
